// src/components/TeacherGroupsView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogContent,
  TextField,
  CircularProgress,
  InputAdornment,
  Avatar,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  AppBar,
  Toolbar,
  Divider,
  Button,
} from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { getRoleLabels, getStoredTutorType } from "../utils/roleTerminology";

/**
 * TeacherGroupsView
 * - IMPORTANT: This component MUST NOT fetch groups (to avoid showing all DB groups).
 * - Source of truth for groups is the `groups` prop, passed from TeacherGroups page.
 * - We fetch students only (for student count + modal), since TeacherGroups passes students as [].
 */
function TeacherGroupView({
  groups = [],
  teachers = [], // should contain ONLY current teacher
  students = [],
  setGroups,
  setStudents,
  // setTeachers not needed on teacher side; keep signature flexible
}) {
  const { t } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [studentListModalOpen, setStudentListModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // -------- Fetch ONLY students (NOT groups / NOT teachers) --------
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const studentsSnapshot = await getDocs(collection(db, "students"));
      setStudents?.(
        studentsSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (error) {
      console.error("TeacherGroupsView: Error loading students:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount (students only)
  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- Search (filters ONLY the groups prop) --------
  const filteredGroups = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return groups || [];

    return (groups || []).filter((group) => {
      const groupName = (group?.name || "").toLowerCase();

      // teachers[] should contain ONLY current teacher on teacher side
      const professor = (teachers || []).find((t) => t.id === group.teacherId);
      const pf = (professor?.firstName || "").toLowerCase();
      const pl = (professor?.lastName || "").toLowerCase();

      return groupName.includes(q) || pf.includes(q) || pl.includes(q);
    });
  }, [groups, teachers, searchQuery]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value || "");
  };

  // -------- Delete group (keeps same functionality) --------
  const handleDeleteGroup = async (groupId) => {
    const confirmDeletion = window.confirm(
      t("Are you sure you want to delete this group? This action is irreversible.")
    );
    if (!confirmDeletion) return;

    try {
      await deleteDoc(doc(db, "groups", groupId));

      // Remove group from local state (teacher-side list)
      setGroups?.((prev) => (prev || []).filter((g) => g.id !== groupId));

      // Remove enrollment entries from local students state (UI-only)
      const updatedStudents = (students || []).map((student) => {
        const updatedEnrollments = (student.enrollments || []).filter(
          (enrollment) => enrollment.groupId !== groupId
        );
        return { ...student, enrollments: updatedEnrollments };
      });
      setStudents?.(updatedStudents);

      // Remove groupId from any teachers that had it (same as your original logic)
      // (Even if only current teacher uses it, we keep functionality identical.)
      const teachersSnapshot = await getDocs(collection(db, "teachers"));
      const teachersToUpdate = teachersSnapshot.docs.filter((teacherDoc) => {
        const teacherData = teacherDoc.data();
        return teacherData.groupIds?.includes(groupId);
      });

      await Promise.all(
        teachersToUpdate.map(async (teacherDoc) => {
          const teacherRef = doc(db, "teachers", teacherDoc.id);
          const updatedGroupIds = (teacherDoc.data().groupIds || []).filter(
            (id) => id !== groupId
          );
          await updateDoc(teacherRef, { groupIds: updatedGroupIds });
        })
      );

      alert(t("Group successfully deleted."));
    } catch (error) {
      console.error("Error deleting group:", error);
      alert(t("An error occurred. Please try again."));
    }
  };

  // -------- View students --------
  const handleViewStudents = (group) => {
    setSelectedGroup(group);
    setStudentListModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedGroup(null);
    setStudentListModalOpen(false);
  };

  const getTeacherName = (teacher) =>
    `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || t("Unknown");

  const getGroupTitle = (group) =>
    `${group?.name || t("Untitled group")} (${group?.level || t("Unspecified")} - ${
    group?.subjectName || t("No subject")
    })`;

  // -------- Count students per group (same logic) --------
  const groupStudentCount = useMemo(() => {
    const map = new Map();
    (groups || []).forEach((g) => map.set(g.id, 0));

    (students || []).forEach((student) => {
      const enrollments = student?.enrollments;
      if (!Array.isArray(enrollments)) return;

      enrollments.forEach((en) => {
        if (!en?.groupId) return;
        if (!map.has(en.groupId)) map.set(en.groupId, 0);
        map.set(en.groupId, (map.get(en.groupId) || 0) + 1);
      });
    });

    return map;
  }, [groups, students]);

  const modalStudents = useMemo(() => {
    if (!selectedGroup) return [];
    return (students || []).filter(
      (student) =>
        Array.isArray(student.enrollments) &&
        student.enrollments.some((enrollment) => enrollment.groupId === selectedGroup.id)
    );
  }, [students, selectedGroup]);

  return (
    <Box
      sx={{
        direction: "rtl",
        backgroundColor: "#fff",
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          gap: 2,
        }}
      >
        {/* RIGHT: Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "#e0f2fe",
              color: "#0369a1",
              width: 46,
              height: 46,
              boxShadow: "0 8px 18px rgba(3,105,161,0.14)",
            }}
          >
            <GroupsIcon />
          </Avatar>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
               {t("Groups and Classes")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {role.isCoach ? t("View and manage your programs inside the platform") : t("View and manage your classes inside the platform")}
            </Typography>
          </Box>
        </Box>

        {/* LEFT: Actions + Search */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: "ltr", flexWrap: "wrap" }}>
          <IconButton
            onClick={fetchStudents}
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.12)",
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#f9fafb" },
            }}
            title={role.isCoach ? t("Refresh clients data") : t("Refresh students data")}
          >
            <LoopIcon sx={{ fontSize: 22, color: "rgba(0,0,0,0.65)" }} />
          </IconButton>

          <TextField
            size="small"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t("Search groups by name...")}
            sx={{
              width: { xs: "100%", sm: 520 },
              backgroundColor: "#f9fafb",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 0 }}>
        <Table sx={{ direction: "rtl" }}>
          <TableHead sx={{ bgcolor: "#ffffff" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Group name")}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{role.tutorLabel}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}> {role.isCoach ? t("Clients count") : t("Students count")}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={26} />
                  <Typography sx={{ mt: 1, color: "text.secondary" }}>
                    {role.isCoach ? t("Loading clients data...") : t("Loading students data...")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (filteredGroups || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {t("No groups match your search.")}
                </TableCell>
              </TableRow>
            ) : (
              (filteredGroups || [])
                .slice(0, searchQuery ? filteredGroups.length : 7)
                .map((group) => {
                  const professor = (teachers || []).find((t) => t.id === group.teacherId);
                  const enrolledStudentsCount = groupStudentCount.get(group.id) || 0;

                  return (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{
                        "& td": { py: 2 },
                        "&:hover": { backgroundColor: "#fafafa" },
                      }}
                    >
                      {/* Group */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: group?.groupImage ? 1.5 : "50%",
                              bgcolor: "rgba(3,105,161,0.10)",
                              color: "#0369a1",
                              fontWeight: 900,
                            }}
                            src={group?.groupImage || undefined}
                          >
                            {(group?.name?.[0] || t("G")).toUpperCase()}
                          </Avatar>

                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>
                              {group?.name || t("Untitled group")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {group?.level || t("Unspecified")} · {group?.subjectName || t("No subject")}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Teacher */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={getTeacherName(professor)}
                          size="small"
                          sx={{
                            bgcolor: "rgba(99,102,241,0.10)",
                            color: "#4f46e5",
                            fontWeight: 800,
                            borderRadius: "5px",
                          }}
                        />
                      </TableCell>

                      {/* Students count */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={role.isCoach ? t("{{count}} clients", { count: enrolledStudentsCount }) : t("{{count}} students", { count: enrolledStudentsCount })}
                          size="small"
                          sx={{
                            bgcolor:
                              enrolledStudentsCount > 0
                                ? "rgba(34,197,94,0.12)"
                                : "rgba(148,163,184,0.18)",
                            color:
                              enrolledStudentsCount > 0
                                ? "rgba(22,163,74,0.95)"
                                : "rgba(100,116,139,0.95)",
                            fontWeight: 800,
                            borderRadius: "5px",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", flexDirection: "row-reverse", gap: 1 }}>
                          <IconButton
                            onClick={() => handleDeleteGroup(group.id)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,0.10)",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                            title={t("Delete group")}
                          >
                            <DeleteIcon />
                          </IconButton>

                          <IconButton
                            onClick={() => handleViewStudents(group)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,0.10)",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                            title={role.isCoach ? t("View clients") : t("View students")}
                          >
                            <PeopleIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal: student list */}
      <Dialog
        open={studentListModalOpen}
        onClose={handleCloseModal}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: 760,
            maxWidth: "95vw",
            direction: "rtl",
            maxHeight: "90vh",
            overflow: "hidden",
          },
        }}
      >
        <AppBar
          position="relative"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Typography sx={{ flexGrow: 1, fontWeight: 900, textAlign: "center" }}>
              {role.isCoach ? t("Enrolled clients information") : t("Enrolled students information")}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 2.5 }}>
          {selectedGroup ? (
            <>
              {/* Group summary */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                  {getGroupTitle(selectedGroup)}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {(role.isCoach ? t("Clients count") : t("Students count"))}: {modalStudents.length}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {modalStudents.length > 0 ? (
                <Box
                  sx={{
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <List sx={{ py: 0, direction: "rtl", textAlign: "right" }}>
                    {modalStudents.map((student) => (
                      <ListItem
                        key={student.id}
                        sx={{
                          py: 1.5,
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <ListItemText
                          sx={{ textAlign: "right" }}
                          primary={
                            <Typography sx={{ fontWeight: 900, textAlign: "right" }}>
                              {student.firstName} {student.lastName}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{ color: "text.secondary", textAlign: "right" }}>
                              {student.email || "-"}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Typography sx={{ display: "flex", justifyContent: "center", color: "text.secondary", py: 4 }}>
                  {role.isCoach ? t("No clients found") : t("No students found")}
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>{t("Please select a group.")}</Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button onClick={handleCloseModal} variant="outlined" color="error" sx={{ borderRadius: 2 }}>
              {t("Close")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TeacherGroupView;
